import { useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useProductSearch } from '@/features/pos/hooks/usePosApi';

/** One editable document line, shaped for the procurement/sales order APIs. */
export interface DocumentLine {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  /**
   * Per-line customisation note — how the customer wants THIS line made.
   * Optional: only documents that opt in via `showNotes` collect one, and
   * the field is simply absent on the rest.
   */
  notes?: string;
}

export function emptyLineFromProduct(product: {
  _id: string;
  name: string;
  sku?: string;
  sellingPrice?: number;
  purchasePrice?: number;
  taxProfileId?: { cgstRate?: number; sgstRate?: number; igstRate?: number };
}, priceField: 'sellingPrice' | 'purchasePrice'): DocumentLine {
  const tax = product.taxProfileId ?? {};
  return {
    productId: product._id,
    productName: product.name,
    sku: product.sku ?? '',
    quantity: 1,
    unitPrice: Number(product[priceField] ?? 0),
    discount: 0,
    cgstRate: Number(tax.cgstRate ?? 0),
    sgstRate: Number(tax.sgstRate ?? 0),
    igstRate: Number(tax.igstRate ?? 0),
  };
}

export function lineNet(line: DocumentLine): number {
  return line.quantity * line.unitPrice - line.discount;
}

export function lineTax(line: DocumentLine): number {
  // CGST+SGST (local) and IGST (interstate) are alternatives, never cumulative.
  const rate = line.cgstRate + line.sgstRate || line.igstRate;
  return (lineNet(line) * rate) / 100;
}

export function documentTotals(lines: DocumentLine[]) {
  const subTotal = lines.reduce((sum, l) => sum + lineNet(l), 0);
  const taxTotal = lines.reduce((sum, l) => sum + lineTax(l), 0);
  return {
    subTotal: Math.round(subTotal * 100) / 100,
    taxTotal: Math.round(taxTotal * 100) / 100,
    grandTotal: Math.round((subTotal + taxTotal) * 100) / 100,
  };
}

/**
 * Reusable product-line editor for purchase orders, sales orders and any other
 * commerce document. Totals shown here are a client-side preview — the server
 * recomputes them authoritatively on save.
 */
export function LineItemsEditor({
  lines,
  onChange,
  priceField = 'sellingPrice',
  priceLabel = 'Rate',
  showPreviewNote = false,
  showNotes = false,
}: {
  lines: DocumentLine[];
  onChange: (lines: DocumentLine[]) => void;
  priceField?: 'sellingPrice' | 'purchasePrice';
  priceLabel?: string;
  showPreviewNote?: boolean;
  /** Opt-in: a per-line notes/specs input under the product name. Off by
   *  default so Procurement's purchase-order table is unchanged. */
  showNotes?: boolean;
}) {
  const [query, setQuery] = useState('');
  const { data: results = [] } = useProductSearch(query);
  const totals = documentTotals(lines);

  const addProduct = (product: Parameters<typeof emptyLineFromProduct>[0]) => {
    const existing = lines.findIndex((l) => l.productId === product._id);
    if (existing >= 0) {
      onChange(lines.map((l, i) => (i === existing ? { ...l, quantity: l.quantity + 1 } : l)));
    } else {
      onChange([...lines, emptyLineFromProduct(product, priceField)]);
    }
    setQuery('');
  };

  const update = (index: number, patch: Partial<DocumentLine>) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  return (
    <div className="flex flex-col gap-3">
      {/* Product search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" aria-hidden />
        <input
          aria-label="Search products to add"
          className="h-10 w-full rounded border border-outline bg-surface pl-9 pr-3 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
          placeholder="Search a product by name or SKU to add a line…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && results.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded border border-outline bg-surface shadow-lg">
            {results.slice(0, 8).map((product: { _id: string; name: string; sku?: string; sellingPrice?: number; purchasePrice?: number }) => (
              <li key={product._id}>
                <button
                  type="button"
                  onClick={() => addProduct(product)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-body-sm hover:bg-surface-variant"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-on-surface">{product.name}</span>
                    <span className="block text-on-surface-variant">{product.sku || 'No SKU'}</span>
                  </span>
                  <span className="shrink-0 text-on-surface-variant">
                    ₹{Number(product[priceField] ?? 0).toLocaleString('en-IN')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lines */}
      {lines.length === 0 ? (
        <p className="rounded-lg border border-dashed border-outline p-4 text-center text-body-sm text-on-surface-variant">
          No lines yet — search above to add products.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <table className="w-full min-w-[680px] text-left text-body-sm">
            <caption className="sr-only">Document lines</caption>
            <thead className="bg-surface-variant text-on-surface-variant">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">Product</th>
                <th scope="col" className="w-24 px-3 py-2 text-right font-medium">Qty</th>
                <th scope="col" className="w-32 px-3 py-2 text-right font-medium">{priceLabel}</th>
                <th scope="col" className="w-20 px-3 py-2 text-right font-medium">Disc</th>
                <th scope="col" className="w-28 px-3 py-2 text-right font-medium">Tax</th>
                <th scope="col" className="w-32 px-3 py-2 text-right font-medium">Line total</th>
                <th scope="col" className="w-12 px-3 py-2"><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={`${line.productId}-${index}`} className="border-t border-outline">
                  <td className="px-3 py-2">
                    <span className="block font-medium text-on-surface">{line.productName}</span>
                    <span className="block text-on-surface-variant">{line.sku || 'No SKU'}</span>
                    {showNotes && (
                      <input
                        type="text"
                        aria-label={`Notes or specs for ${line.productName}`}
                        placeholder="Add item notes / specs..."
                        value={line.notes ?? ''}
                        onChange={(e) => onChange(lines.map((l, i) => (
                          i === index ? { ...l, notes: e.target.value } : l
                        )))}
                        className="mt-1 w-full rounded-md border border-outline/70 bg-surface/50 px-2 py-1 text-body-xs italic text-on-surface-variant placeholder:not-italic placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      aria-label={`Quantity for ${line.productName}`}
                      type="number"
                      min="1"
                      className="text-right"
                      value={String(line.quantity)}
                      onChange={(e) => update(index, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      aria-label={`${priceLabel} for ${line.productName}`}
                      type="number"
                      min="0"
                      step="0.01"
                      className="text-right"
                      value={String(line.unitPrice)}
                      onChange={(e) => update(index, { unitPrice: Math.max(0, Number(e.target.value) || 0) })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      aria-label={`Discount for ${line.productName}`}
                      type="number"
                      min="0"
                      step="0.01"
                      className="text-right"
                      value={String(line.discount)}
                      onChange={(e) => update(index, { discount: Math.max(0, Number(e.target.value) || 0) })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right text-on-surface-variant">
                    ₹{lineTax(line).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-on-surface">
                    ₹{(lineNet(line) + lineTax(line)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={`Remove ${line.productName}`}
                      onClick={() => onChange(lines.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-outline bg-surface-variant/50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-on-surface-variant">Subtotal</td>
                <td colSpan={2} className="px-3 py-2 text-right font-medium text-on-surface">
                  ₹{totals.subTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-on-surface-variant">Tax</td>
                <td colSpan={2} className="px-3 py-2 text-right font-medium text-on-surface">
                  ₹{totals.taxTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right font-semibold text-on-surface">Grand total</td>
                <td colSpan={2} className="px-3 py-2 text-right text-body-md font-bold text-on-surface">
                  ₹{totals.grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {showPreviewNote && (
        <p className="text-xs text-muted-foreground mt-1">
          Totals shown are a preview and will be calculated on submission.
        </p>
      )}
    </div>
  );
}
